<!-- src/admin/pages/JobsPage.vue -->
<template>
    <!-- No extra grid or min‐height here. This is the right‐column content. -->
    <header class="overview-header">
        <h1>Job Postings</h1>
        <button class="add-button" @click="toggleForm">
            {{ isEditing ? 'Cancel Edit' : (showForm ? 'Close Form' : 'Add New Job') }}
        </button>
    </header>

    <!-- Add / Edit Form -->
    <section v-if="showForm" class="form-section">
        <h2>{{ isEditing ? 'Edit Job' : 'New Job' }}</h2>
        <form @submit.prevent="saveJob">
            <div class="form-group">
                <label for="title">Title</label>
                <input id="title" v-model="jobForm.title" type="text" required />
            </div>
            <div class="form-group">
                <label for="company">Company</label>
                <select id="company" v-model="jobForm.company" required>
                    <option value="" disabled>Select a company</option>
                    <option value="RTH Cleaners">RTH Cleaners</option>
                    <option value="RTH Group">RTH Group</option>
                    <option value="All Seasons Exchange Agency">All Seasons Exchange Agency</option>
                    <option value="VaDaBa">VaDaBa</option>
                </select>
            </div>
            <div class="form-group">
                <label for="location">Location</label>
                <input id="location" v-model="jobForm.location" type="text" required />
            </div>
            <div class="form-group">
                <label for="description">Description</label>
                <textarea id="description" v-model="jobForm.description" rows="4" required></textarea>
            </div>
            <div class="form-group">
                <label for="postedAt">Posted At</label>
                <input v-model="jobForm.postedDate" type="date" required />
            </div>
            <div class="form-actions">
                <button type="submit" class="save-button">
                    {{ isEditing ? 'Update Job' : 'Create Job' }}
                </button>
            </div>
        </form>
    </section>

    <!-- Jobs Table -->
    <section class="jobs-table-section">
        <div class="table-wrap">
            <table>
                <thead>
                    <tr>
                        <th>Title</th>
                        <th>Company</th>
                        <th>Location</th>
                        <th>Date Posted</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="job in jobs" :key="job.id">
                        <td>{{ job.title }}</td>
                        <td>{{ job.company }}</td>
                        <td>{{ job.location }}</td>
                        <td>{{ formatDate(job.postedAt) }}</td>
                        <td>
                            <button class="action-button edit" @click="startEdit(job)">Edit</button>
                            <button class="action-button delete" @click="deleteJob(job.id)">
                                Delete
                            </button>
                        </td>
                    </tr>
                    <tr v-if="jobs.length === 0">
                        <td colspan="5" class="no-data">No job postings found.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </section>
</template>

<script setup>
import { confirmAction } from '@/services/notify'
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { collection, onSnapshot, addDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore'
import { db, auth } from '@/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { useRouter } from 'vue-router'

// Router + Auth
const router = useRouter()

// Reactive state
const jobs = ref([])
let unsubscribeSnapshot = null

// Form state
const showForm = ref(false)
const isEditing = ref(false)
const editingJobId = ref(null)
const jobForm = reactive({
    title: '',
    company: '',
    location: '',
    description: '',
    postedDate: '' // "YYYY-MM-DD"
})

// Listen for auth state and fetch jobs
onMounted(() => {
    const unwatchAuth = onAuthStateChanged(auth, (user) => {
        if (!user) {
            router.replace({ name: 'Login' })
        } else {
            const jobsCol = collection(db, 'jobs')
            unsubscribeSnapshot = onSnapshot(jobsCol, (snapshot) => {
                jobs.value = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }))
            })
        }
        unwatchAuth()
    })
})

onUnmounted(() => {
    if (unsubscribeSnapshot) {
        unsubscribeSnapshot()
    }
})

// Toggle the add/edit form
function toggleForm() {
    if (isEditing.value) {
        // If currently editing, cancel the edit
        resetForm()
    }
    showForm.value = !showForm.value
}

// Prepare form for editing a job
function startEdit(job) {
    isEditing.value = true
    editingJobId.value = job.id

    // Fill in existing data
    jobForm.title = job.title
    jobForm.company = job.company
    jobForm.location = job.location
    jobForm.description = job.description
    const d = job.postedAt.seconds
        ? new Date(job.postedAt.seconds * 1000)
        : new Date(job.postedAt)
    jobForm.postedDate = d.toISOString().substr(0, 10)

    showForm.value = true
}

// Save (create or update) a job
async function saveJob() {
    if (
        !jobForm.title.trim() ||
        !jobForm.company.trim() ||
        !jobForm.location.trim() ||
        !jobForm.description.trim() ||
        !jobForm.postedDate
    ) {
        return
    }

    const payload = {
        title: jobForm.title.trim(),
        company: jobForm.company.trim(),
        location: jobForm.location.trim(),
        description: jobForm.description.trim(),
        postedAt: new Date(jobForm.postedDate),
    }

    try {
        if (isEditing.value && editingJobId.value) {
            const jobRef = doc(db, 'jobs', editingJobId.value)
            await updateDoc(jobRef, payload)
        } else {
            await addDoc(collection(db, 'jobs'), payload)
        }
    } catch (e) {
        console.error('Error saving job:', e)
    }

    resetForm()
}

// Delete a job
async function deleteJob(jobId) {
    if (!(await confirmAction({ title: 'Delete this job listing?', text: 'It will be removed from the public jobs page.', confirmText: 'Delete', danger: true }))) return
    try {
        await deleteDoc(doc(db, 'jobs', jobId))
    } catch (e) {
        console.error('Error deleting job:', e)
    }
}

// Reset form
function resetForm() {
    jobForm.title = ''
    jobForm.company = ''
    jobForm.location = ''
    jobForm.description = ''
    jobForm.postedDate = ''
    isEditing.value = false
    editingJobId.value = null
    showForm.value = false
}

// Format Firestore Timestamp to “MMM D, YYYY”
function formatDate(val) {
    if (!val) return '—'
    const d = val.seconds ? new Date(val.seconds * 1000) : new Date(val)
    return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    })
}
</script>

<style scoped>
/* Header */
.overview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
}

.overview-header h1 {
    font-size: 24px;
    color: #1f2937;
}

.add-button {
    background-color: #2563eb;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.add-button:hover {
    background-color: #1e40af;
}

/* Form Section */
.form-section {
    background-color: white;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 32px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.form-section h2 {
    margin-bottom: 16px;
    font-size: 20px;
    color: #111827;
}

.form-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
}

.form-group label {
    margin-bottom: 4px;
    font-size: 14px;
    color: #374151;
}

.form-group input,
.form-group textarea {
    padding: 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 14px;
}

.form-group textarea {
    resize: vertical;
}

.form-actions {
    margin-top: 16px;
}

.save-button {
    background-color: #10b981;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
}

.save-button:hover {
    background-color: #059669;
}

/* Jobs Table Section */
.jobs-table-section .table-wrap {
    overflow-x: auto;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

table {
    width: 100%;
    border-collapse: collapse;
}

thead {
    background-color: #e5e7eb;
}

th,
td {
    padding: 12px 16px;
    text-align: left;
    color: #374151;
}

th {
    font-size: 12px;
    text-transform: uppercase;
    color: #6b7280;
}

tbody tr {
    border-bottom: 1px solid #e5e7eb;
}

.action-button {
    margin-right: 8px;
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
}

.action-button.edit {
    background-color: #3b82f6;
    color: white;
}

.action-button.edit:hover {
    background-color: #2563eb;
}

.action-button.delete {
    background-color: #ef4444;
    color: white;
}

.action-button.delete:hover {
    background-color: #dc2626;
}

.no-data {
    text-align: center;
    padding: 16px;
    color: #6b7280;
}

/* Responsive */
@media (max-width: 768px) {
    .overview-header {
        flex-direction: column;
        align-items: stretch;
    }

    .add-button {
        margin-top: 12px;
        width: 100%;
    }

    .form-section,
    .jobs-table-section .table-wrap {
        box-shadow: none;
    }
}
</style>
